import React, { useMemo, useState } from 'react';

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
  rectIntersection,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CombineType, DraggableTeamItem } from './DraggableTeamItem';
import { ClassTeam } from '../ClassesTag/ClassesTag';
import { TeamItem } from './TeamItem';
import { ConfirmModal } from '../../../../../components';
import { Notification } from '../../../../../hooks/useNotification';
import { TypeError } from '../../../../../types/enums';
import { useTranslation } from 'react-i18next';

export interface Props {
  items: ClassTeam[];
  combineTeams: (sourceTeamId: number, targetTeamId: number) => Promise<ClassTeam>;
  //
  push: (obj: any) => void;
  remove: (index: number) => void;
  classId: number | string;
  setField: (field: string, value: any, shouldValidate?: boolean) => void;
  canCombine: (sourceId: UniqueIdentifier, targetId: UniqueIdentifier) => boolean;
  disableCombine: boolean;
}

export function SortableWithCombine({
  items,
  combineTeams,
  //
  push,
  remove,
  classId,
  setField,
  canCombine,
  disableCombine,
}: Props) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [combineTargetId, setCombineTargetId] = useState<UniqueIdentifier | null>(null);
  const [betweenTargetIndex, setBetweenTargetIndex] = useState<number | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const getIndex = (id: UniqueIdentifier) => items.findIndex((t) => t.id === id);
  const activeIndex = activeId ? getIndex(activeId) : -1;
  const [confirmMergeState, setConfirmMergeState] = useState<{
    sourceItem: ClassTeam;
    targetItem: ClassTeam;
    fullState: ClassTeam[];
  } | null>(null);


  const combinable = useMemo(() => {
    if (!combineTargetId || !activeId) {
      return false;
    }
    if (combineTargetId === activeId) {
      return false;
    }

    return canCombine(combineTargetId, activeId);
  }, [combineTargetId, activeId, canCombine]);

  const confirmMerge = async () => {
    try {
      const sourceId = confirmMergeState?.sourceItem.id;
      const targetId = confirmMergeState?.targetItem.id;
      if (!sourceId || !targetId || typeof sourceId !== 'number' || typeof targetId !== 'number') {
        throw new Error('Source or target item not found');
      }
      const newTeam = await combineTeams(sourceId, targetId);
      const updatedTeams = items.filter((team) => team.id.toString() !== confirmMergeState?.sourceItem.id.toString()).map((team) => {
        if (team.id === confirmMergeState?.targetItem.id) {
          return newTeam;
        }
        return team;
      });
      setField('teams', updatedTeams);
      Notification(TypeError.success, t(prefix + 'combineTeams-success'));
    } catch (error) {
      console.error('Failed to combine teams:', error);
      Notification(TypeError.error, t(prefix + 'combineTeams-error'));
      setField('teams', confirmMergeState?.fullState ?? []);
    }
    setConfirmMergeState(null);
  }

  const cancelMerge = () => {
    setField('teams', confirmMergeState?.fullState ?? []);
    setConfirmMergeState(null);
  }

  const BetweenDropTarget = ({ index, disabled }: { index: number, disabled: boolean }) => {
    const id = `between-${index}`;
    const { isOver, setNodeRef, active } = useDroppable({ id, disabled });
    return (
      <div
        ref={setNodeRef}
        style={{
          transition: 'all 0.3s ease',
          height: isOver ? 68 : 20,
          margin: '0.25rem 0',
          borderRadius: 4,
          background: (isOver || betweenTargetIndex === index) ? 'rgba(0,0,0,0.1)' : 'transparent',
          border: active && !disabled ? '1px dashed rgba(0,0,0,0.2)' : 'none'
        }}
      >
        {isOver && (
          <div style={{ margin: '-0.25rem 0' }}>
            <TeamItem
              team={items[activeIndex]}
              index={activeIndex}
              push={push}
              remove={remove}
              classId={classId}
              teams={items}
              setField={setField}
              combineStatus={combineTargetId !== null ? combinable ? CombineType.COMBINABLE : CombineType.NOT_COMBINABLE : null}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ConfirmModal
        opened={!!confirmMergeState}
        onConfirm={confirmMerge}
        onCancel={cancelMerge}
        text={t(prefix + 'confirmMergeTeams')}
        description={t(prefix + 'confirmMergeTeamsDescription')}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={({ active }) => {
          if (!active) {
            return;
          }

          setActiveId(active.id);
        }}
        onDragOver={({ over, active }) => {
          if (!over) {
            setBetweenTargetIndex(null);
            setCombineTargetId(null);
            return;
          }
          const overId = over.id as UniqueIdentifier;
          const overIdStr = String(overId);
          if (overIdStr.startsWith('between-')) {
            const index = parseInt(overIdStr.replace('between-', ''), 10);
            setBetweenTargetIndex(Number.isNaN(index) ? null : index);
            setCombineTargetId(null);
            return;
          }
          // Dropping over an item: prepare combine state/UI
          if (overId !== active.id && !disableCombine) {
            setCombineTargetId(overId);
            setBetweenTargetIndex(null);
            return;
          }
          setCombineTargetId(null);
          setBetweenTargetIndex(null);
        }}
        onDragEnd={({ over, active }) => {
          if (over) {
            const overIdStr = String(over.id);
            if (overIdStr.startsWith('between-')) {
              const dropIndex = parseInt(overIdStr.replace('between-', ''), 10);
              if (activeIndex !== -1 && !Number.isNaN(dropIndex)) {
                const destinationIndex = activeIndex < dropIndex ? dropIndex - 1 : dropIndex;
                const updatedList = arrayMove(items, activeIndex, destinationIndex);
                setField(
                  'teams',
                  updatedList.map((t, i) => {
                    if (t.seed) {
                      t.seed = i + 1;
                    }
                    return t;
                  })
                );
              }
            } else if (combineTargetId !== null && combineTargetId !== activeId && combinable) {
              const targetIndex = items.findIndex((t) => t.id === combineTargetId);
              if (activeIndex !== -1 && targetIndex !== -1) {
                setConfirmMergeState({
                  sourceItem: items[activeIndex],
                  targetItem: items[targetIndex],
                  fullState: items,
                });
              }
            }
          }
          setActiveId(null);
          setCombineTargetId(null);
          setBetweenTargetIndex(null);
        }}
        onDragCancel={() => {
          setActiveId(null);
          setCombineTargetId(null);
          setBetweenTargetIndex(null);
        }}
      >
        <div>
          {/* Drop target before first item */}
          <BetweenDropTarget index={0} disabled={activeIndex === 0} />
          {items.map((team, index) => {
            return (
              <React.Fragment key={team.id}>
                <DraggableTeamItem
                  team={team}
                  index={index}
                  push={push}
                  remove={remove}
                  classId={classId}
                  teams={items}
                  setField={setField}
                  canCombineFn={canCombine}
                />
                {/* Drop target after each item (between index and index+1) */}
                <BetweenDropTarget index={index + 1} disabled={activeIndex === index || activeIndex === index + 1} />
              </React.Fragment>
            );
          })}
        </div>
      </DndContext>
    </>
  );
}